import {
  type CSSProperties, type FC, type RefObject,
} from 'react';
import dynamic from 'next/dynamic';
import { useTranslation } from 'next-i18next';
import classNames from 'classnames/bind';
import { NEAR_ME } from 'src/common/constants';
import Icon from 'src/common/components/IconDS22';
import { useSearchInputContext, type SearchRefs } from 'src/common/components/Search/SearchInputProvider';
import { useSearchHeroModalContext } from 'src/common/components/Search/SearchHero/SearchHeroModalProvider';
import useDynamicHeight from 'src/common/components/Search/LocationAutocompleteList/useDynamicPopoverHeight';
import useOnclickOutside from 'react-cool-onclickoutside';
import useAfterRenderCallback from 'src/common/hooks/useAfterRenderCallback';
import { useSearchComboboxContext } from 'src/common/components/Search/SearchComboboxProvider';
import type { SearchHeroVariant } from 'src/common/components/Search/types';
import styles from './LocationAutocompleteList.module.scss';

const RecentSearches = dynamic(
  () => import('src/search/components/RecentSearches'),
  {
    loading: () => <div />,
    ssr: false, // Only works with localStorage
  },
);

const cx = classNames.bind(styles);

type LocationAutocompleteListProps = {
  shadow?: boolean;
  withRecentSearches?: boolean;
  refs: Partial<SearchRefs>;
  variant?: SearchHeroVariant;
  popoverRef: RefObject<HTMLDivElement>;
};
/**
 * This must be rendered when the sibling input is rendered for downshift to match
 * Even if this menu is display none until input is focused
 * */
const LocationAutocompleteList: FC<LocationAutocompleteListProps> = ({
  shadow = true,
  withRecentSearches = true,
  refs,
  variant,
  popoverRef,
}) => {
  const { t } = useTranslation('common');
  const {
    combobox, places, recentSearches,
  } = useSearchComboboxContext();
  const { isDesktop } = useSearchInputContext();
  const { close, openType } = useSearchHeroModalContext();

  const isOpen = combobox.isOpen && openType !== 'open-sheet';
  const popoverHeight = useDynamicHeight({ ref: popoverRef, isOpen, isDesktop });

  const blurAfterRender = useAfterRenderCallback(() => {
    if (refs?.inputs?.current?.contains(document.activeElement)) return;
    if (variant === 'sheet') return; // else click mobile footer prevents leftButton handler

    close();
  });

  const closeOutsideRef = useOnclickOutside(() => {
    blurAfterRender();
  }, { disabled: !isOpen, refs: refs?.inputs ? [refs.inputs] : [] });

  return (
    <div
      className={cx(styles.root, `${variant}Variant`, { open: isOpen, shadow })}
      ref={popoverRef}
      style={{
        '--popover-height': `${popoverHeight}px`,
      } as CSSProperties}
    >

      <div ref={closeOutsideRef}>
        <ul {...combobox.getMenuProps()} className={styles.placesResults}>
          {isOpen && places.map((place, index) => {
            const { key, ...itemProps } = combobox.getItemProps({
              key: place.place_id,
              item: place,
              index,
            });
            return (
              <li
                key={key}
                {...itemProps}
                className={cx(styles.item, { active: combobox.highlightedIndex === index })}
              >
                <button className={styles.listButton} type="button" onClick={close} tabIndex={-1}>
                  <div className={styles.iconCircle}>
                    <Icon
                      id={
                   `${place.place_id === NEAR_ME
                     ? 'gps-filled'
                     : 'pin-filled'}`
                   }
                      size="l"
                    />
                  </div>
                  <span className={styles.itemText}>
                    {t(`search--description--${place.place_id}`, place.description)}
                  </span>
                </button>
              </li>
            );
          })}
        </ul>
        {!!recentSearches.length && withRecentSearches && (
          <RecentSearches />
        )}
      </div>
    </div>
  );
};

export default LocationAutocompleteList;
